import { createFileRoute } from "@tanstack/react-router";
import { useLocale, useTranslations } from "next-intl";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useVehicleDetail } from "@/hooks/use-vehicle-detail";
import { formatDate } from "@/lib/format";

function VehicleDetailSkeleton() {
  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="space-y-1">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-4 w-32" />
            </div>
          ))}
        </CardContent>
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <Skeleton className="h-5 w-28" />
          </CardHeader>
          <CardContent className="space-y-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-32" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <Skeleton className="h-5 w-24" />
          </CardHeader>
          <CardContent className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

export const Route = createFileRoute("/_dashboard/vehicles/$id")({
  component: VehicleDetailPage,
});

function VehicleDetailPage() {
  const { id } = Route.useParams();
  const t = useTranslations("VehicleDetail");
  const locale = useLocale();
  const tKind = useTranslations("VehicleKind");
  const { data: detail, isLoading, isFetching, isError, refetch } =
    useVehicleDetail(id);

  if (isLoading) {
    return (
      <>
        <PageHeader
          title="..."
          parent={{ label: t("breadcrumbParent"), href: "/vehicles" }}
        />
        <VehicleDetailSkeleton />
      </>
    );
  }

  if (isError) {
    return (
      <>
        <PageHeader
          title="—"
          parent={{ label: t("breadcrumbParent"), href: "/vehicles" }}
        />
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-4 text-center">
          <h1 className="text-muted-foreground text-base font-normal">
            {t("loadError")}
          </h1>
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? t("retrying") : t("retry")}
          </Button>
        </div>
      </>
    );
  }

  if (!detail) {
    return (
      <>
        <PageHeader
          title="—"
          parent={{ label: t("breadcrumbParent"), href: "/vehicles" }}
        />
        <div className="flex flex-1 items-center justify-center p-4">
          <h1 className="text-muted-foreground text-base font-normal">
            {t("notFound")}
          </h1>
        </div>
      </>
    );
  }

  const { vehicle, owner, policies } = detail;
  const na = t("notAvailable");

  return (
    <>
      <PageHeader
        title={vehicle.license_plate}
        parent={{ label: t("breadcrumbParent"), href: "/vehicles" }}
      />
      <div className="flex flex-1 flex-col gap-4 p-4">
        <h1 className="sr-only">{vehicle.license_plate}</h1>
        {/* Vehicle Info Card */}
        <Card>
          <CardHeader>
            <h2 className="leading-none font-semibold">{t("vehicleInfo")}</h2>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <InfoField label={t("licensePlate")}>
                <span className="font-mono">{vehicle.license_plate}</span>
              </InfoField>
              <InfoField label={t("make")}>{vehicle.make}</InfoField>
              <InfoField label={t("model")}>{vehicle.model}</InfoField>
              <InfoField label={t("kind")}>
                <Badge variant="outline">
                  {tKind.has(vehicle.kind as "car")
                    ? tKind(vehicle.kind as "car")
                    : vehicle.kind}
                </Badge>
              </InfoField>
              <InfoField label={t("vin")}>{vehicle.vin ?? na}</InfoField>
              <InfoField label={t("year")}>{vehicle.year ?? na}</InfoField>
              <InfoField label={t("color")}>{vehicle.color ?? na}</InfoField>
              <InfoField label={t("registrationCountry")}>
                {vehicle.registration_country ?? na}
              </InfoField>
              <InfoField label={t("createdAt")}>
                {formatDate(vehicle.created_at, locale)}
              </InfoField>
              <InfoField label={t("updatedAt")}>
                {formatDate(vehicle.updated_at, locale)}
              </InfoField>
            </dl>
          </CardContent>
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Owner Card */}
          <Card>
            <CardHeader>
              <h2 className="leading-none font-semibold">{t("owner")}</h2>
            </CardHeader>
            <CardContent>
              {owner ? (
                <dl className="grid gap-4 sm:grid-cols-2">
                  <InfoField label={t("ownerName")}>{owner.name}</InfoField>
                  <InfoField label={t("ownerType")}>
                    <Badge variant="secondary">
                      {owner.entity.type === "person"
                        ? t("person")
                        : t("organization")}
                    </Badge>
                  </InfoField>
                </dl>
              ) : (
                <p className="text-muted-foreground text-sm">
                  {vehicle.owner ? t("ownerUnavailable") : t("noOwner")}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Policies Card */}
          <Card>
            <CardHeader>
              <h2 className="leading-none font-semibold">{t("policies")}</h2>
            </CardHeader>
            <CardContent>
              {policies.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  {t("noPolicies")}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("policyNumber")}</TableHead>
                      <TableHead>{t("policyCompany")}</TableHead>
                      <TableHead>{t("policyExpiresAt")}</TableHead>
                      <TableHead>{t("policyKind")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {policies.map((policy) => (
                      <TableRow key={policy.id}>
                        <TableCell className="font-mono">
                          {policy.number}
                        </TableCell>
                        <TableCell>{policy.company}</TableCell>
                        <TableCell>
                          {formatDate(policy.expires_at, locale)}
                        </TableCell>
                        <TableCell>{policy.kind ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
